import * as THREE from 'three';

interface Particle {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  active: boolean;
}

export class VfxSystem {
  readonly root = new THREE.Group();
  private pool: Particle[] = [];
  private reducedMotion = false;
  private reducedFlash = false;

  constructor(poolSize = 120) {
    const geo = new THREE.SphereGeometry(0.06, 6, 6);
    for (let i = 0; i < poolSize; i++) {
      const mesh = new THREE.Mesh(
        geo,
        new THREE.MeshBasicMaterial({
          color: 0xffffff,
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
        })
      );
      mesh.visible = false;
      this.root.add(mesh);
      this.pool.push({
        mesh,
        vel: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
        active: false,
      });
    }
  }

  setAccessibility(reducedMotion: boolean, reducedFlash: boolean): void {
    this.reducedMotion = reducedMotion;
    this.reducedFlash = reducedFlash;
  }

  private acquire(): Particle | null {
    return this.pool.find((p) => !p.active) ?? null;
  }

  burst(
    pos: THREE.Vector3,
    color: number,
    count = 12,
    speed = 1.5,
    life = 0.45
  ): void {
    if (this.reducedMotion) count = Math.min(4, count);
    if (this.reducedFlash) return;
    for (let i = 0; i < count; i++) {
      const p = this.acquire();
      if (!p) break;
      p.active = true;
      p.life = life * (0.7 + Math.random() * 0.3);
      p.maxLife = p.life;
      p.mesh.visible = true;
      p.mesh.position.copy(pos);
      p.mesh.scale.setScalar(0.6 + Math.random() * 0.8);
      (p.mesh.material as THREE.MeshBasicMaterial).color.setHex(color);
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = 0.95;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      p.vel.set(
        Math.sin(phi) * Math.cos(theta) * speed * (0.5 + Math.random()),
        Math.sin(phi) * Math.sin(theta) * speed * (0.5 + Math.random()),
        Math.cos(phi) * speed * 0.4
      );
    }
  }

  ring(pos: THREE.Vector3, color: number, radius = 0.5): void {
    if (this.reducedFlash) return;
    const count = this.reducedMotion ? 8 : 16;
    for (let i = 0; i < count; i++) {
      const p = this.acquire();
      if (!p) break;
      const a = (i / count) * Math.PI * 2;
      p.active = true;
      p.life = 0.35;
      p.maxLife = 0.35;
      p.mesh.visible = true;
      p.mesh.position.set(pos.x + Math.cos(a) * radius * 0.2, pos.y + Math.sin(a) * radius * 0.2, pos.z);
      (p.mesh.material as THREE.MeshBasicMaterial).color.setHex(color);
      p.vel.set(Math.cos(a) * 2, Math.sin(a) * 2, 0.3);
    }
  }

  update(dt: number): void {
    for (const p of this.pool) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        p.mesh.visible = false;
        continue;
      }
      p.mesh.position.addScaledVector(p.vel, dt);
      p.vel.multiplyScalar(0.96);
      p.vel.z -= dt * 0.8;
      const t = p.life / p.maxLife;
      (p.mesh.material as THREE.MeshBasicMaterial).opacity = t;
      p.mesh.scale.setScalar(0.5 + t * 0.8);
    }
  }

  dispose(): void {
    // geometries shared; materials per mesh
    for (const p of this.pool) {
      (p.mesh.material as THREE.Material).dispose();
    }
  }
}
